import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSpaces } from '../context/SpaceContext';
import { getMyAttention, getMyTasks, getMyCalendar, getMyFiles } from '../services/meService';
import api from '../services/api';
import './DashboardPage.css';

export default function DashboardPage() {
  const { user } = useAuth();
  const { spaces, loading: spacesLoading } = useSpaces();
  const [unreadCount, setUnreadCount] = useState(null);

  const [attention, setAttention] = useState({ overdueTasks: [], myReportingObligations: [], pendingApprovals: [] });
  const [attentionLoading, setAttentionLoading] = useState(true);

  const [recentTasks, setRecentTasks] = useState([]);
  const [upcomingItems, setUpcomingItems] = useState([]);
  const [recentFiles, setRecentFiles] = useState([]);

  useEffect(() => {
    api.get('/inbox/unread-count')
      .then((res) => setUnreadCount(res.data.count))
      .catch(() => setUnreadCount(0));

    getMyAttention()
      .then((res) => setAttention(res.data.attention || {}))
      .catch((err) => console.error('Failed to load attention:', err))
      .finally(() => setAttentionLoading(false));

    getMyTasks()
      .then((res) => setRecentTasks((res.data.tasks || []).slice(0, 5)))
      .catch((err) => console.error('Failed to load tasks:', err));

    getMyCalendar()
      .then((res) => setUpcomingItems((res.data.items || []).slice(0, 5)))
      .catch((err) => console.error('Failed to load calendar:', err));

    getMyFiles()
      .then((res) => setRecentFiles((res.data.files || []).slice(0, 5)))
      .catch((err) => console.error('Failed to load files:', err));
  }, []);

  const totalAttentionCount =
    (attention.overdueTasks?.length || 0) +
    (attention.myReportingObligations?.length || 0) +
    (attention.pendingApprovals?.length || 0);

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="dashboard-title-row">
          <div>
            <h1>My Work & Workspace</h1>
            <p className="dashboard-sub">Personal consolidated view across all authorized Businesses, Projects & Spaces</p>
          </div>
          <div className="user-badge-pill">
            <span className="user-badge-dot"></span>
            <span>{user?.name} ({user?.role})</span>
          </div>
        </div>
      </div>

      {/* ── My Attention Section ── */}
      <section className={`dash-attention-card ${totalAttentionCount > 0 ? 'has-attention' : ''}`}>
        <div className="dash-attention-header">
          <h2>
            <span className="attention-icon">🔔</span> My Attention
            {totalAttentionCount > 0 && <span className="attention-badge">{totalAttentionCount}</span>}
          </h2>
          <span className="muted-text">Items needing your action or review</span>
        </div>

        {attentionLoading ? (
          <p className="muted">Loading attention items...</p>
        ) : totalAttentionCount === 0 ? (
          <div className="dash-attention-empty">
            <span className="check-icon">✓</span>
            <span>All caught up! No overdue tasks, pending approvals, or urgent reporting obligations.</span>
          </div>
        ) : (
          <div className="attention-items-grid">
            {attention.overdueTasks?.length > 0 && (
              <div className="attention-group">
                <div className="attention-group-title overdue">Overdue Tasks ({attention.overdueTasks.length})</div>
                <ul className="attention-list">
                  {attention.overdueTasks.map((task) => (
                    <li key={task._id}>
                      <Link to="/tasks" className="attention-link">
                        <span className="item-title">{task.title}</span>
                        <span className="item-badge space-badge">{task.spaceId?.name || 'Space Task'}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {attention.pendingApprovals?.length > 0 && (
              <div className="attention-group">
                <div className="attention-group-title pending">Pending Expense Approvals ({attention.pendingApprovals.length})</div>
                <ul className="attention-list">
                  {attention.pendingApprovals.map((exp) => (
                    <li key={exp._id}>
                      <Link to="/projects" className="attention-link">
                        <span className="item-title">{exp.description || 'Expense submission'}</span>
                        <span className="item-amount">${exp.amount?.toLocaleString()} {exp.currency}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {attention.myReportingObligations?.length > 0 && (
              <div className="attention-group">
                <div className="attention-group-title upcoming">Reporting Obligations ({attention.myReportingObligations.length})</div>
                <ul className="attention-list">
                  {attention.myReportingObligations.map((ob) => (
                    <li key={ob._id}>
                      <Link to="/calendar" className="attention-link">
                        <span className="item-title">{ob.title}</span>
                        <span className="item-badge obligation-badge">Due {new Date(ob.dueDate).toLocaleDateString()}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Main Dashboard Grid ── */}
      <div className="dashboard-grid">
        {/* Consolidated My Tasks */}
        <section className="dash-card">
          <div className="dash-card-header">
            <h2>My Assigned Tasks</h2>
            <Link to="/tasks" className="view-all-link">View all →</Link>
          </div>
          {recentTasks.length === 0 ? (
            <p className="muted">No active tasks assigned to you.</p>
          ) : (
            <ul className="dash-item-list">
              {recentTasks.map((t) => (
                <li key={t._id} className="dash-task-item">
                  <div className="task-info">
                    <span className="task-title">{t.title}</span>
                    <span className="context-pill">{t.spaceId?.name || 'Space'}</span>
                  </div>
                  <span className={`status-tag status-${t.status || 'todo'}`}>{t.status || 'todo'}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Consolidated My Calendar & Deadlines */}
        <section className="dash-card">
          <div className="dash-card-header">
            <h2>My Calendar & Deadlines</h2>
            <Link to="/calendar" className="view-all-link">View all →</Link>
          </div>
          {upcomingItems.length === 0 ? (
            <p className="muted">No upcoming events or deadlines.</p>
          ) : (
            <ul className="dash-item-list">
              {upcomingItems.map((item, idx) => (
                <li key={item.sourceId || idx} className="dash-cal-item">
                  <div className="cal-info">
                    <span className="cal-title">{item.title}</span>
                    <span className="context-pill">{item.spaceName || 'Project'}</span>
                  </div>
                  <span className={`cal-type-tag type-${(item.sourceType || 'event').toLowerCase()}`}>
                    {item.sourceType} • {new Date(item.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Consolidated My Files */}
        <section className="dash-card">
          <div className="dash-card-header">
            <h2>My Files</h2>
            <Link to="/files" className="view-all-link">View all →</Link>
          </div>
          {recentFiles.length === 0 ? (
            <p className="muted">No files in your authorized spaces.</p>
          ) : (
            <ul className="dash-item-list">
              {recentFiles.map((f) => (
                <li key={f._id} className="dash-file-item">
                  <span className="file-icon">📄</span>
                  <div className="file-info">
                    <span className="file-name">{f.originalName || f.name}</span>
                    <span className="context-pill">{f.spaceId?.name || 'File'}</span>
                  </div>
                  <span className="file-size">{((f.size || 0) / 1024).toFixed(0)} KB</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Authorized Spaces */}
        <section className="dash-card">
          <div className="dash-card-header">
            <h2>My Spaces ({spaces.length})</h2>
          </div>
          {spacesLoading ? (
            <p className="muted">Loading spaces...</p>
          ) : spaces.length === 0 ? (
            <p className="muted">No spaces yet.</p>
          ) : (
            <ul className="dash-space-list">
              {spaces.map((space) => (
                <li key={space._id}>
                  <Link to={`/spaces/${space._id}`} className="dash-space-link">
                    <span className="dash-space-badge">{space.name[0]?.toUpperCase()}</span>
                    <div>
                      <div className="dash-space-name">{space.name}</div>
                      <div className="dash-space-type">{space.businessId ? 'Business Space' : space.type || 'Workspace'}</div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Quick links footer */}
      <section className="dash-quick-bar">
        <Link to="/inbox" className="quick-link">
          ✉ Inbox {unreadCount > 0 && <span className="dash-unread-badge">{unreadCount}</span>}
        </Link>
        <Link to="/inbox" className="quick-link">Messages</Link>
        <Link to="/work" className="quick-link">Work</Link>
        <Link to="/people" className="quick-link">People</Link>
      </section>
    </div>
  );
}
